/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import useRouter from 'js/lib/useRouter';

import { Typography2 } from '@coursera/cds-core';

import type { ManuallyGradedQuestion } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { getRubricComponent } from 'bundles/grading/components/staff-graded-assignments/form-parts/utils';
import NotificationsPanel from 'bundles/grading/components/staff-graded-assignments/notifications/NotificationsPanel';
import AssignmentGradeSummary from 'bundles/grading/components/staff-graded-assignments/right-panel/AssignmentGradeSummary';
import IntegrityFlag from 'bundles/grading/components/staff-graded-assignments/right-panel/IntegrityFlag';
import QuestionRubricSelector from 'bundles/grading/components/staff-graded-assignments/right-panel/QuestionRubricSelector';
import RubricProgress from 'bundles/grading/components/staff-graded-assignments/right-panel/RubricProgress';
import SGALazyLoadRightPanel from 'bundles/grading/components/staff-graded-assignments/right-panel/SGALazyLoadRightPanel';
import {
  hasUnsubmittedChanges,
  isAutoGradedQuestion,
  isManuallyGradedQuestion,
} from 'bundles/grading/utils/submissionUtils';

import _t from 'i18n!nls/grading';

const styles = {
  container: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  main: css`
    height: 100%;
  `,
  section: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);

    &:not(:first-of-type) {
      border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  infoBlock: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
    padding: var(--cds-spacing-200) var(--cds-spacing-200);
    border-radius: 8px;

    p {
      max-width: 70%;
      margin: 0 auto;
      text-align: center;
      color: var(--cds-color-neutral-primary-weak);
    }
  `,
  gradingSection: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
};

/**
 * SGA Grading: right panel contents
 */

const SGARightPanel = () => {
  const { getUrl, submissionData, isPrivateSession } = useContext(SGAPageContext);

  const { gradebookEntry, maxScore, rawScore, submission, submitter } = submissionData.data || {};

  const { autoSaveTaskStates, reviewPartProgress } = useContext(AutoSaveContext);

  const currentAutoSaveTask = autoSaveTaskStates[submitter?.submitterId || ''];

  const router = useRouter();

  if (submissionData.loading) {
    return <SGALazyLoadRightPanel />;
  }
  if (submissionData.error) {
    return null;
  }

  const currentSubmitterId = submitter?.id as string;
  const handleItemClick = (questionId: string, rubricId?: string) => {
    return () => router.push(getUrl(currentSubmitterId, questionId, rubricId));
  };

  const RubricComponent = submission?.currentRubric ? getRubricComponent(submission.currentRubric) : null;
  const unsubmittedChanges =
    hasUnsubmittedChanges(submitter?.submissionStatus, submitter?.isSubmissionGraded) &&
    !currentAutoSaveTask?.isSubmitting;
  // Show rubrics only if submission exists, and it's not auto-graded submission with 0 assignment rubrics.
  const showRubrics = submission && !(submission.isAutoGradedOnly && submission.assignmentRubrics.length === 0);

  const autoGradedQuestion = submission?.currentQuestion ? isAutoGradedQuestion(submission?.currentQuestion) : false;
  const manualGradedQuestion = submission?.currentQuestion
    ? isManuallyGradedQuestion(submission?.currentQuestion)
    : false;
  const totalRubrics = (submission?.currentQuestion as ManuallyGradedQuestion)?.questionReviewParts?.length || 0;

  return (
    <div css={styles.container}>
      <div css={styles.main}>
        <div css={styles.section}>
          <AssignmentGradeSummary
            submitterId={currentSubmitterId}
            submissionStatus={submitter?.submissionStatus}
            rawScore={rawScore}
            maxScore={maxScore}
            gradebookEntry={gradebookEntry}
          />
          {isPrivateSession && (
            <div css={styles.gradingSection}>
              {showRubrics && (
                <RubricProgress
                  reviewPartProgress={reviewPartProgress}
                  hasUnsubmittedChanges={unsubmittedChanges}
                  gradesSubmittedAtLeastOnce={gradebookEntry?.originalOutcome !== null}
                  currentSubmitterId={currentSubmitterId}
                  rubricMap={submission.rubricMap}
                  getUrlCallback={getUrl}
                />
              )}
              {submission && <IntegrityFlag submitterId={currentSubmitterId} submitterName={submitter?.name} />}
            </div>
          )}
        </div>
        {showRubrics ? (
          <div css={styles.section}>
            <QuestionRubricSelector
              submission={submission}
              reviewPartProgress={reviewPartProgress}
              onClick={handleItemClick}
            />
            <NotificationsPanel />
            {submission.currentRubric && RubricComponent && <RubricComponent rubric={submission.currentRubric} />}
          </div>
        ) : (
          <NotificationsPanel />
        )}

        {!totalRubrics && (autoGradedQuestion || manualGradedQuestion || submission?.isAutoGradedOnly) && (
          <div css={styles.section}>
            <div css={styles.infoBlock}>
              <Typography2 variant="subtitleMedium" component="p">
                {autoGradedQuestion &&
                  _t(
                    "Since this is an auto-graded question, it doesn't have a rubric. You don't need to grade it manually."
                  )}
                {manualGradedQuestion &&
                  _t("This question doesn't have a rubric. You don't need to grade it manually.")}
                {submission?.isAutoGradedOnly &&
                  _t("This assignment is fully auto-graded. You don't need to grade it manually.")}
              </Typography2>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SGARightPanel;
